export const isDropMedicine = (medicine?: { type?: string } | null) =>
  /\bdrops?\b/i.test(medicine?.type || "");

export const isLiquidMedicine = (medicine?: { type?: string } | null) =>
  !isDropMedicine(medicine) && /\b(syrups?|suspensions?|syp|susp)\b/i.test(medicine?.type || "");

export const isBottledMedicine = (medicine?: { type?: string } | null) =>
  isDropMedicine(medicine) || isLiquidMedicine(medicine);

export const doseAmount = (value?: string | number) => {
  const amount = Number(value);
  return Number.isFinite(amount) && amount > 0 ? amount : 1;
};

export const medicineDoseConfig = (medicine?: { type?: string } | null) => {
  const type = String(medicine?.type || "").trim().toLowerCase();
  const base = { unit: "count", plural: "count", decimal: false, custom: false, autoQuantity: true, quantityLabel: "Medicine count" };
  if (/^(tablets?|tab)$/.test(type)) return { ...base, decimal: true };
  if (isDropMedicine(medicine)) return { ...base, unit: "drop", plural: "drops", autoQuantity: false, quantityLabel: "Bottle quantity" };
  if (isLiquidMedicine(medicine) || ["solution", "mouthwash"].includes(type)) return { ...base, unit: "ml", plural: "ml", decimal: true, autoQuantity: false, quantityLabel: "Bottle quantity" };
  if (["ointment", "cream", "gel", "lotion"].includes(type)) return { ...base, unit: "application", plural: "applications", autoQuantity: false, quantityLabel: type === "lotion" ? "Bottle quantity" : "Tube / container quantity" };
  if (type === "inhaler") return { ...base, unit: "puff", plural: "puffs", autoQuantity: false, quantityLabel: "Inhaler quantity" };
  if (type === "spray") return { ...base, unit: "spray", plural: "sprays", autoQuantity: false, quantityLabel: "Container quantity" };
  if (["injection", "infusion", "nebulizer solution", "powder", "granules"].includes(type)) return { ...base, custom: true, autoQuantity: false, quantityLabel: "Dispensing quantity (vials / bags / packs)" };
  const units: Record<string, [string, string]> = {
    sachet: ["sachet", "sachets"], suppository: ["suppository", "suppositories"],
    pessary: ["pessary", "pessaries"], patch: ["patch", "patches"], lozenge: ["lozenge", "lozenges"],
  };
  if (units[type]) return { ...base, unit: units[type][0], plural: units[type][1], quantityLabel: `Quantity (${units[type][1]})` };
  return base;
};

export const doseLabel = (item: {
  count_per_time?: string | number;
  dosage?: string;
  medicine?: { type?: string } | null;
}) => {
  const amount = doseAmount(item.count_per_time);
  const config = medicineDoseConfig(item.medicine);
  if (config.custom) return item.dosage?.trim() || "Dose not specified";
  const fractions: Record<string, string> = { "0.5": "1/2", "0.75": "3/4" };
  const displayAmount = config.unit === "count" ? fractions[String(amount)] ?? String(amount) : String(amount);
  return `${displayAmount} ${amount === 1 ? config.unit : config.plural}`;
};

export const formatDoseSchedule = (item: {
  frequency?: string;
  dosage?: string;
  count_per_time?: string | number;
  medicine?: { type?: string } | null;
}) => {
  const frequency = String(item.frequency || "").trim();
  const count = 1;
  const parts = frequency.split("|").map((part) => part.trim());
  const numericFrequency = Number(parts[parts.length - 1]);
  const schedules: Record<string, string> = {
    "OD-M": `${count}-0-0`,
    "OD-A": `0-${count}-0`,
    "OD-N": `0-0-${count}`,
    "1": `${count}-0-0`,
    "2": `${count}-0-${count}`,
    "3": `${count}-${count}-${count}`,
    "4": `${count}-${count}-${count}-${count}`,
  };
  const schedule = schedules[frequency] || schedules[String(numericFrequency)] || parts[0] || "-";
  return `${doseLabel(item)} / ${schedule}`;
};

export const isWeeklyFrequency = (frequency?: string) => /\bQWK\b|once a week/i.test(frequency || "");

// Stored durations stay in days so existing prescriptions retain their meaning.
export const prescriptionDurationLabel = (duration?: string, frequency?: string) => {
  if (!duration) return "-";
  const days = Number(duration);
  return isWeeklyFrequency(frequency) && days > 0 && days % 7 === 0
    ? `${days / 7} weeks`
    : `${duration} days`;
};
